import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IMergedFloorView, IMinimizedProfession } from '@d-orca/shared/trusstor';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle } from '@utils/professionColor';
import { HOIST_OPERATOR_TRADE_ID, isOperatorInHoist, msToHoistTimeString } from '@utils/hoistUtils';
import locationIcon from '@assets/icons/location/location.svg';
import locationCrossedIcon from '@assets/icons/location/location-crossed.svg';
import styles from './mainView.module.css';

type IFloorHoistTooltipContentProps = {
  hoists: IMergedFloorView<IMinimizedProfession>[];
};

/**
 * Local narrow over the hoist record. The base `IMergedFloorView` extends
 * a Zod-`.passthrough()` schema, so its declared string/Date fields are
 * widened to `{}` after `Omit<IFloorView, 'areas'>` resolves. Centralise
 * the cast here so the JSX stays readable.
 */
type HoistRecord = {
  floorId: string;
  floorNick?: string;
  matchingFixedFloorChangeTimestamp?: Date | string;
  areas?: { areaNick?: string }[];
  managers: IMergedFloorView<IMinimizedProfession>['managers'];
  activeProfessions: IMergedFloorView<IMinimizedProfession>['activeProfessions'];
};

function asHoist(h: IMergedFloorView<IMinimizedProfession>): HoistRecord {
  return h as unknown as HoistRecord;
}

/**
 * Body of the hoist hover tooltip. One block per hoist serving the same
 * fixed floor; each block has a header (area nick + time-spent), an
 * operator status row, and the crew chips (managers + non-operator
 * professions with worker counts) — or "no workers" when empty.
 *
 * Mirrors trusstor's `HoistIcon.tsx:62-176`. Differences vs trusstor:
 * - `getHoistTimePastFromStartDay` / `isWorkDayOver` branches dropped;
 *   we always show plain elapsed time. d-orca doesn't yet plumb
 *   `IProject.workDayHours` / `tz` into the monitor state.
 * - The operator status uses a green/grey dot (no PNG icon) since
 *   d-orca doesn't have the location-icon assets.
 */
export function FloorHoistTooltipContent({ hoists }: IFloorHoistTooltipContentProps) {
  const { t } = useTranslation();
  // Capture "now" once when the tooltip mounts. The popup is short-lived
  // (created on hover, destroyed on close) so this is accurate enough for
  // displaying time-on-floor. `useState`'s lazy initialiser also keeps
  // ESLint's `react-hooks/purity` happy — `Date.now()` runs once at mount,
  // never during render.
  const [now] = useState(() => Date.now());

  return (
    <div className={styles.hoistTooltipBody}>
      {hoists.map((rawHoist, idx) => {
        const hoist = asHoist(rawHoist);
        // Trusstor's hoist tooltip resolves the area name via a
        // separate `projectAreas` query. d-orca's IMergedFloorView
        // already carries the per-hoist area on `floor.areas[0]` (each
        // hoist floor has exactly one area in the shape we receive),
        // so use it directly. Fall back to `floorNick` if absent.
        const areaNick = hoist.areas?.[0]?.areaNick ?? hoist.floorNick ?? '';
        const tsRaw = hoist.matchingFixedFloorChangeTimestamp;
        const tsDate = tsRaw instanceof Date ? tsRaw : tsRaw ? new Date(tsRaw) : null;
        const elapsed = tsDate ? Math.max(0, now - tsDate.getTime()) : 0;
        const timeLabel = tsDate ? msToHoistTimeString(elapsed, t) : null;
        const operatorPresent = isOperatorInHoist(rawHoist);
        // Trusstor sorts by `[sortIndex, name]` (HoistIcon.tsx:133-135);
        // d-orca's IManagerMergedTag doesn't expose `sortIndex` so fall
        // back to localeCompare-by-name — same fallback used by
        // `selectManagerListRows` for the LeftRail.
        const managers = (hoist.managers ?? []).slice().sort((a, b) => {
          const an = a.manager?.name ?? '';
          const bn = b.manager?.name ?? '';
          return an.localeCompare(bn);
        });
        const crewProfessions = hoist.activeProfessions.filter(
          (p) => (p as { tradeId?: string }).tradeId !== HOIST_OPERATOR_TRADE_ID,
        );
        const empty = managers.length === 0 && crewProfessions.length === 0;

        return (
          <div key={hoist.floorId} className={styles.hoistTooltipBlock}>
            <header className={styles.hoistTooltipHeader}>
              <span className={styles.hoistTooltipAreaNick}>{areaNick}</span>
              {timeLabel && (
                <span className={styles.hoistTooltipTime}>
                  <span className={styles.hoistTooltipTimeLabel}>
                    {t('monitor.buildingView.timeSpentOnFloor')}
                  </span>
                  <span className={styles.hoistTooltipTimeValue}>{timeLabel}</span>
                </span>
              )}
            </header>

            <div className={styles.hoistTooltipOperatorRow}>
              {/* Same asset trusstor uses (`HoistIcon.tsx:112`):
                  `locationIcon` (green pin) when the operator is on the
                  hoist, `locationCrossedIcon` (grey pin with diagonal
                  slash) otherwise. SVGs copied verbatim from
                  `trusstor_dashboard/frontend/src/assets/locationIcons/`. */}
              <img
                className={styles.hoistTooltipOperatorIcon}
                src={operatorPresent ? locationIcon : locationCrossedIcon}
                alt=""
              />
              <span>{t('monitor.buildingView.hoistOperator')}</span>
            </div>

            {empty ? (
              <p className={styles.hoistTooltipEmpty}>
                {t('monitor.buildingView.noWorkersFound')}
              </p>
            ) : (
              <div className={styles.hoistTooltipChips}>
                {managers.map((m) => {
                  const mgr = m.manager;
                  if (!mgr?.name) return null;
                  return (
                    <span
                      key={`${hoist.floorId}-mgr-${m.tagId}`}
                      className={styles.hoistTooltipManagerChip}
                      style={professionChipStyle(mgr.backgroundColor)}
                    >
                      <span className={styles.hoistTooltipManagerAvatar}>
                        {nameInitials(mgr.name)}
                      </span>
                      <span className={styles.hoistTooltipManagerName}>{mgr.name}</span>
                    </span>
                  );
                })}
                {crewProfessions.map((p) => {
                  const tradeName =
                    (p as { specialty?: string; tradeGroup?: string }).specialty ??
                    (p as { tradeGroup?: string }).tradeGroup ??
                    p.tradeGroup ??
                    'Unknown';
                  return (
                    <span
                      key={`${hoist.floorId}-prof-${p._id}`}
                      className={styles.hoistTooltipProfChip}
                      style={professionChipStyle(
                        (p as { backgroundColor?: string }).backgroundColor,
                      )}
                    >
                      <span className={styles.hoistTooltipProfCount}>
                        {p.activeTagsIds.length}
                      </span>
                      <span className={styles.hoistTooltipProfLabel}>{tradeName}</span>
                    </span>
                  );
                })}
              </div>
            )}

            {idx < hoists.length - 1 && <hr className={styles.hoistTooltipDivider} />}
          </div>
        );
      })}
    </div>
  );
}
